<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <app-cells v-if="page_loaded">
      <app-form-group class="form-group--no-indent" label="Finance manager notes" style="width: 100%">
        <app-textarea
          v-model="payment_info.note"
          rows="5"
          @change.native="onUpdatePaymentData({ note: payment_info.note })"
          placeholder="Write a notes"
          :readonly="!is_general_manager"
        />
      </app-form-group>
    </app-cells>
    <div v-if="page_loaded" class="downloads">
      <div class="downloads__item" :class="{'downloads__item--loading': payment_completed_loader}">
        <div class="downloads__left">
          <div class="downloads__info">
            <span class="downloads__name downloads__name--no-events">Completed</span>
          </div>
        </div>
        <div class="downloads__right">
          <div class="downloads__values">
            <span class="downloads__key">Status:</span>
            <app-switcher
              :readonly="!is_general_manager"
              v-model="payment_info.completed"
              @change="onUpdatePaymentData({completed: payment_info.completed}, true)"
            />
          </div>
          <template v-if="payment_info.completed && !payment_completed_loader">
            <div class="downloads__values">
              <span class="downloads__key">Completed at:</span>
              <span class="downloads__value downloads__width">{{ dateTimeFormat(payment_info.completed_at) }}</span>
            </div>
            <div class="downloads__values">
              <span class="downloads__key">Completed by:</span>
              <div class="downloads__person downloads__width">
                <div class="downloads__photo">
                  <img :src="getUserAvatar(payment_info.completed_by?.crop.crop_url)" alt="Person">
                </div>
                <span class="downloads__value">{{ payment_info.completed_by?.full_name || '-' }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-if="page_loaded" class="info-cards">
      <div class="info-cards__card info-cards__card--max550">
        <div class="info-cards__row">
          <div class="info-cards__row-item">
            <span>Amount: </span>
            <span>{{ final_itinerary_info ? `${final_itinerary_info.itinerary_amount_usd.toLocaleString()} USD` : '-' }}</span>
          </div>
          <div class="info-cards__row-item">
            <span>Paid: </span>
            <span>{{ final_itinerary_info ? `${final_itinerary_info.invoices_amount_paid_usd.toLocaleString()} USD` : '-' }}</span>
          </div>
        </div>
        <div class="info-cards__row">
          <div class="info-cards__row-item">
            <span>Balance: </span>
            <span
              class="status-tag"
              :style="{ 'backgroundColor': final_itinerary_info.balance === 0 ? '#77b017' : final_itinerary_info.balance < 0 ? '#e85c20' : '#439ad1' }"
            >
              {{ final_itinerary_info ? `${final_itinerary_info.balance.toLocaleString()} USD` : '-' }}
            </span>
          </div>
          <div class="info-cards__row-item">
            <span>Payable: </span>
            <span
              v-if="!final_itinerary_info.payable_amount_cover_balance"
              class="status-tag"
              :style="{ 'backgroundColor': '#e85c20' }"
            >
              {{ final_itinerary_info ? `${final_itinerary_info.invoices_amount_payable_usd.toLocaleString()} USD` : '-' }}
            </span>
            <span v-else>{{ final_itinerary_info ? `${final_itinerary_info.invoices_amount_payable_usd.toLocaleString()} USD` : '-' }}</span>
          </div>
        </div>
      </div>
    </div>
    <app-cells v-if="page_loaded">
      <app-button @click.native="onRefreshInvoices" ref="refresh">Refresh invoices</app-button>
    </app-cells>
    <template v-if="page_loaded && table.items.length">
      <app-cells><h2 class="title">Not checked</h2></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader || table_all_preloader }"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="` https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </a>
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #status="{ row }">
          <div class="table-default__left table-default__controls">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
          </div>
        </template>
        <template #deadline="{ row }">
          {{ dateFormat(row.deadline) }}
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #paid="{ row }">
          {{ row.amount_paid_usd ? `${row.amount_paid_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #method="{ row }">
          <div class="table-default__left">
            {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
          </div>
        </template>
        <template #checked="{ row }">
          <app-checkbox
            v-if="!table_preloader"
            @change.native="onUpdateChecked(row)"
            v-model="row.payment_control_info.checked"
            class="checkbox--table"
          />
        </template>
        <template #checked_by="{ row }">
          <div v-if="row.payment_control_info?.checked_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.payment_control_info?.checked_by?.crop.crop_url)"
                alt="Avatar"
                v-tooltip="{ content: row.payment_control_info?.checked_by?.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.payment_control_info?.checked_at) }}
          </div>
          <span v-else>-</span>
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_checked.items.length">
      <app-cells><h2 class="title">Checked</h2></app-cells>
      <v-client-table
        :data="table_checked.items"
        :columns="table_checked.columns"
        :options="table_checked.options"
        class="table-default"
        :class="{ 'table-default--loading': table_checked_preloader || table_all_preloader }"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="` https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </a>
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #status="{ row }">
          <div class="table-default__left table-default__controls">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
          </div>
        </template>
        <template #deadline="{ row }">
          {{ dateFormat(row.deadline) }}
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #paid="{ row }">
          {{ row.amount_paid_usd ? `${row.amount_paid_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #method="{ row }">
          <div class="table-default__left">
            {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
          </div>
        </template>
        <template #checked="{ row }">
          <app-checkbox
            v-if="!table_checked_preloader"
            @change.native="onUpdateChecked(row)"
            v-model="row.payment_control_info.checked"
            class="checkbox--table"
          />
        </template>
        <template #checked_by="{ row }">
          <div v-if="row.payment_control_info?.checked_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.payment_control_info?.checked_by?.crop.crop_url)"
                alt="Avatar"
                v-tooltip="{ content: row.payment_control_info?.checked_by?.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.payment_control_info?.checked_at) }}
          </div>
          <span v-else>-</span>
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_refunds.items.length">
      <app-cells><h2 class="title">Refunds</h2></app-cells>
      <v-client-table
        :data="table_refunds.items"
        :columns="table_refunds.columns"
        :options="table_refunds.options"
        class="table-default"
        :class="{ 'table-default--loading': table_all_preloader }"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="` https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </a>
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #paid="{ row }">
          {{ row.amount_paid_usd ? `${row.amount_paid_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #method="{ row }">
          <div class="table-default__left">
            {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
          </div>
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_sent.items.length">
      <app-cells v-if="page_loaded">
        <h2 class="title">Sent invoices</h2>
        <app-button @click.native="onCopyFamilyLinks" size="small">Copy all links</app-button>
      </app-cells>
      <v-client-table
        :data="table_sent.items"
        :columns="table_sent.columns"
        :options="table_sent.options"
        class="table-default"
        :class="{ 'table-default--loading': table_all_preloader }"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </a>
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #payable="{ row }">
          {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #charge="{ row }">{{ row.charge_type.name }}</template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_cancelled.items.length">
      <app-cells><h2 class="title">Cancelled invoices</h2></app-cells>
      <v-client-table
        :data="table_cancelled.items"
        :columns="table_cancelled.columns"
        :options="table_cancelled.options"
        class="table-default"
        :class="{ 'table-default--loading': table_all_preloader }"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="` https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </a>
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #payable="{ row }">
          {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #method="{ row }">
          <div class="table-default__left">
            {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
          </div>
        </template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import {
  getFinanceInvoiceList,
  getRefreshInvoices,
  patchClientsGroupsPaymentControl,
  patchFinanceInvoicePaymentControl,
} from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'MyClientsGroupsPaymentControl',
  props: {
    groupData: Object,
  },
  data() {
    return {
      page_loaded: false,
      payment_info: {},
      final_itinerary_info: {},
      payment_completed_loader: false,
      table_all_preloader: false,
      table_preloader: false,
      table_checked_preloader: false,
      table: {
        items: [],
        columns: [
          'number',
          'payer',
          'status',
          'deadline',
          'amount',
          'paid',
          'method',
          'checked',
          'checked_by',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            status: 'Status',
            deadline: 'Deadline',
            amount: 'Amount',
            paid: 'Paid',
            method: 'Method',
            checked: 'Checked',
            checked_by: 'Checked by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_checked: {
        items: [],
        columns: [
          'number',
          'payer',
          'status',
          'deadline',
          'amount',
          'paid',
          'method',
          'checked',
          'checked_by',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            status: 'Status',
            deadline: 'Deadline',
            amount: 'Amount',
            paid: 'Paid',
            method: 'Method',
            checked: 'Checked',
            checked_by: 'Checked by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_refunds: {
        items: [],
        columns: [
          'number',
          'payer',
          'amount',
          'paid',
          'method',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            amount: 'Amount',
            paid: 'Paid',
            method: 'Method',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_sent: {
        items: [],
        columns: [
          'number',
          'payer',
          'deadline',
          'amount',
          'payable',
          'charge',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            deadline: 'Deadline',
            amount: 'Amount',
            payable: 'Payable',
            charge: 'Charge type',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_cancelled: {
        items: [],
        columns: [
          'number',
          'payer',
          'deadline',
          'amount',
          'payable',
          'method',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            deadline: 'Deadline',
            amount: 'Amount',
            payable: 'Payable',
            method: 'Method',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  watch: {
    groupData: {
      handler(value) {
        this.payment_info = value.payment_control_info
        if (value.lead?.final_itinerary) this.final_itinerary_info = value.lead?.final_itinerary
      },
      immediate: true
    }
  },
  created() {
    Promise.allSettled([this.fetchItems(), this.fetchItemsOther()])
      .then(() => { this.page_loaded = true })
  },
  methods: {
    onRefreshInvoices() {
      this.$refs.refresh.preload = true
      this.table_all_preloader = true
      getRefreshInvoices(this.groupData.lead.id)
        .then(() => {
          this.$emit('update-group')
          this.fetchItems()
          this.fetchItemsOther()
          this.$refs.refresh.preload = false
        })
        .catch(() => { this.$refs.refresh.preload = false })
    },
    fetchItems() {
      return Promise.allSettled([
        getFinanceInvoiceList({payment_control_checked: false, client_group: +this.$route.params.id, status: 'paid,partially'})
          .then(response => {
            this.table.items = response.data
            this.table_preloader = false
          }),
        getFinanceInvoiceList({payment_control_checked: true, client_group: +this.$route.params.id, status: 'paid,partially'})
          .then(response => {
            this.table_checked.items = response.data
            this.table_checked_preloader = false
          })
      ])
    },
    fetchItemsOther() {
      return Promise.allSettled([
        getFinanceInvoiceList({client_group: +this.$route.params.id, status: 'refund'})
          .then(response => {
            this.table_refunds.items = response.data
          }),
        getFinanceInvoiceList({client_group: +this.$route.params.id, status: 'sent'})
          .then(response => {
            this.table_sent.items = response.data
          }),
        getFinanceInvoiceList({client_group: +this.$route.params.id, status: 'cancel'})
          .then(response => {
            this.table_cancelled.items = response.data
            this.table_all_preloader = false
          })
      ])
    },
    onUpdateChecked(row) {
      this.table_preloader = true
      this.table_checked_preloader = true
      patchFinanceInvoicePaymentControl(row.id, {checked: row.payment_control_info.checked})
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Changes saved',
          })
        })
    },
    onUpdatePaymentData(data, loader = false) {
      if (loader) this.payment_completed_loader = true
      patchClientsGroupsPaymentControl(+this.$route.params.id, data)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.$emit('update-group')
          if (loader) this.payment_completed_loader = false
        })
        .catch(() => { if (loader) this.payment_completed_loader = false })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onCopyFamilyLinks() {
      let string = ''
      this.table_sent.items.forEach((item, index, array) => {
        string += `${item.payer}: https://story.altezza.travel/invoice/shared/${item.hash}${index === array.length - 1 ? '' : '\n'}`
      })
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Links copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  },
}
</script>

<style scoped lang="sass">

</style>
